import FlashMessage from "@/components/FlashMessage";
import Seomatic from "@/components/Seomatic";
import SignUpForm from "@/components/SignUpForm";
import AuthLayout from "@/layouts/AuthLayout";
import craftBatch from "@/lib/craftBatch";
import getCommonServerSideProps from "@/lib/getCommonServerSideProps";
import QueryOrganisation from "@/queries/craft/QueryOrganisation.graphql";
import QuerySignUp from "@/queries/craft/QuerySignUp.graphql";
import clsx from "clsx";
import { getServerSession } from "next-auth/next";
import { useSession } from "next-auth/react";
import Head from "next/head";
import { authOptions } from "pages/api/auth/[...nextauth]";
import React from "react";

export async function getServerSideProps({
  preview,
  params,
  req,
  res,
  query,
  locale,
}) {
  // Fetch the common props
  const commonProps = await getCommonServerSideProps({
    req,
    res,
    query,
    locale,
  });

  const session = await getServerSession(req, res, authOptions);

  // If we have a sesstion, redirect to account page
  if (session) {
    return {
      redirect: {
        destination: "/welcome",
        permanent: false,
      },
    };
  }

  const { site } = commonProps;

  // if we are on a subdomain, redirect to the signin page on the main domain
  if (site !== "default") {
    return {
      redirect: {
        destination: `${process.env.NEXTAUTH_URL}/auth/signup?organisationUid=${commonProps.organisation?.uid}`,
        permanent: false,
      },
    };
  }

  const { organisationUid } = query || {};

  const variables = {};

  if (!!organisationUid) variables.uid = organisationUid;
  if (site !== "default") variables.slug = site;

  const queries = [
    {
      document: QuerySignUp,
    },
    {
      document: QueryOrganisation,
      variables,
    },
  ];

  const response = await craftBatch(queries);

  const { pageEntry } = response[0];

  const organisation =
    (site !== "default" || organisationUid) && response[1]
      ? response[1].organisation
      : null;

  return {
    props: {
      organisation,
      session,
      pageEntry,
    },
  };
}

const SignUp = ({ organisations, organisation, pageEntry }) => {
  const { data: session, status } = useSession();
  const loading = status === "loading";

  if (typeof window !== "undefined" && loading) return null;

  // If we have no organisation, return a flash message
  if (!organisation)
    return (
      <div className={clsx("py-8")}>
        <FlashMessage>
          We need to know your organisation before you join. Please{" "}
          <a href="mailto:hello@ycn.org" className={clsx("underline")}>
            contact us
          </a>{" "}
          to get an invitation.
        </FlashMessage>
      </div>
    );

  return (
    <>
      <Seomatic Head={Head} {...pageEntry.seomatic} />
      <Head>
        <meta key="robots" name="robots" content="none" />
      </Head>
      <SignUpForm organisations={organisations} organisation={organisation} />
    </>
  );
};

const SignUpLayout = (page) => {
  const subHeading = page.props.organisation
    ? `Enjoy unlimited access as part of the ${page.props.organisation.title} team`
    : "Become a partner today";

  return (
    <AuthLayout heading="Sign up" subHeading={subHeading} showSignUpLink>
      {page}
    </AuthLayout>
  );
};

SignUp.getLayout = SignUpLayout;

SignUp.displayName = "SignUp";

export default SignUp;
